import { Pencil, Disc3, ClipboardList, UserRound } from "lucide-react";
import ConfirmDelete from "@/components/ConfirmDelete";
import { phaseLabel, phaseTone } from "./dprUtils";

const fmt = (n) => (Number(n) || 0).toLocaleString();

export function ProductionLog({ entries, showDate, isOp, canEdit, onEdit, onDeleteStage }) {
  if (!entries.length) {
    return (
      <div className="section-card text-center py-14" data-testid="dpr-log-empty">
        <ClipboardList size={36} className="mx-auto text-slate-300" />
        <p className="mt-3 text-slate-500 text-sm">No production recorded {showDate ? "in this period" : "today"} yet.</p>
        {canEdit && <p className="text-xs text-slate-400 mt-1">Tap <b>Record production</b> to add the first entry.</p>}
      </div>
    );
  }
  let lastDate = null;
  return (
    <div className="section-card !p-0 overflow-hidden" data-testid="dpr-log">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left py-3 px-4">Machine / Process</th>
            <th className="text-left py-3 px-4">Product</th>
            <th className="text-right py-3 px-4">In</th>
            <th className="text-right py-3 px-4">Rejected</th>
            <th className="text-right py-3 px-4">Good</th>
            <th className="text-left py-3 px-4 hidden md:table-cell">Notes</th>
            <th className="py-3 px-4"></th>
          </tr>
        </thead>
        <tbody data-testid="dpr-log-body">
          {entries.map((e) => {
            const tone = phaseTone(e.phase);
            const dateRow = showDate && e.date !== lastDate; lastDate = e.date;
            return [
              dateRow && (
                <tr key={`d-${e.date}`} className="bg-slate-50/70"><td colSpan="7" className="px-4 py-1.5 text-xs font-semibold text-slate-500" data-testid={`dpr-log-date-${e.date}`}>{new Date(e.date).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</td></tr>
              ),
              <tr key={e.sid || `${e.dpr_id}-${e.process_id}`} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`dpr-log-row-${e.sid}`}>
                <td className="py-2.5 px-4">
                  <div className="flex items-center gap-2">
                    <span className={`w-1 h-8 rounded-full ${tone.bar}`} />
                    <div>
                      <div className="font-semibold text-slate-800">{e.process_name}</div>
                      <div className="text-xs text-slate-400">{phaseLabel(e.phase)} · {e.shift}{e.coil_id ? <span className="inline-flex items-center gap-1 ml-1.5 text-amber-600"><Disc3 size={11} /> coil{e.coil_wastage_kg ? ` · ${e.coil_wastage_kg} kg waste` : ""}</span> : null}</div>
                    </div>
                  </div>
                </td>
                <td className="py-2.5 px-4 text-slate-600">
                  <div>{e.model}</div>
                  {(e.operator_name || e.start_time) && <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5" data-testid={`dpr-log-operator-${e.sid}`}><UserRound size={11} /> {e.operator_name || "—"}{e.start_time || e.end_time ? ` · ${e.start_time || "?"}–${e.end_time || "?"}` : ""}</div>}
                </td>
                <td className="py-2.5 px-4 text-right font-mono text-slate-700">{fmt(e.input_qty)}</td>
                <td className={`py-2.5 px-4 text-right font-mono ${e.rejected_qty > 0 ? "text-red-600" : "text-slate-400"}`}>{fmt(e.rejected_qty)}</td>
                <td className="py-2.5 px-4 text-right font-mono font-semibold text-emerald-700">{fmt(e.good_qty)}{e.multiplier && e.multiplier !== 1 ? <span className="text-[10px] text-slate-400 font-sans ml-1">×{e.multiplier}</span> : null}</td>
                <td className="py-2.5 px-4 text-slate-500 text-xs hidden md:table-cell max-w-[200px] truncate">{e.remarks || (e.repolish_qty ? `repolish ${fmt(e.repolish_qty)}` : "—")}</td>
                <td className="py-2.5 px-4 text-right">
                  {canEdit && e.sid && (
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`dpr-log-edit-${e.sid}`} onClick={() => onEdit(e)} className="p-1.5 rounded text-slate-500 hover:text-blue-600 hover:bg-blue-50" title="Edit entry"><Pencil size={15} /></button>
                      <ConfirmDelete testId={`dpr-log-delete-${e.sid}`} requireRemark={isOp} onConfirm={(rmk) => onDeleteStage(e, rmk)} title="Remove this entry?" description="This entry will be removed and its stock movement reversed." />
                    </div>
                  )}
                </td>
              </tr>,
            ];
          })}
        </tbody>
      </table>
    </div>
  );
}
